import { useUser } from "@/api/users"
import { PaymentLink } from "@/api/payment-links"
import { useEffect, useRef, useState } from "react"
import DesktopIcon from "@/components/icons/monitor.svg"
import PhoneIcon from "@/components/icons/iphone-x-apps.svg"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import Loader from "@/components/layout/loader"
import BrowserTop from "@/components/browser-top"
import BrowserTopMobile from "@/components/browser-top-mobile"
import AutoScale from "@/components/ux/auto-scale"

interface PayPreviewProps {
  paymentLink: PaymentLink;
  className?: string;
}

type PreviewDevice = "desktop" | "mobile";

const deviceSizes = {
  desktop: { width: 1280, height: 900 },
  mobile: { width: 500, height: 1000 },
}

export default function PayPreview({ paymentLink }: PayPreviewProps) {
  const { user } = useUser()
  const [device, setDevice] = useState<PreviewDevice>("desktop")
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [previewLoading, setPreviewLoading] = useState(true)

  const deviceWidth = deviceSizes[device].width
  const deviceHeight = deviceSizes[device].height

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: "payment-link",
        data: {
          ...paymentLink,
          user: {
            ...user,
            wallet: {
              id: "1",
              address: "0x1234567890",
            },
          },
        },
      },
      window.origin,
    )
  }, [paymentLink, user, previewLoading])

  return (
    <div className="flex bg-gray-50 flex-col flex-grow overflow-hidden w-full h-full p-10">
      <div className="flex flex-col gap-4 flex-grow items-stretch justify-center min-h-0">
        <div className="flex flex-row justify-between items-center pb-2 gap-4">
          <h4 className="text-lg font-semibold">Preview</h4>
          <ToggleGroup
            type="single"
            value={device}
            defaultValue={device}
            onValueChange={(value) =>
              value && setDevice(value as PreviewDevice)
            }
          >
            <ToggleGroupItem value="desktop">
              <img src={DesktopIcon} alt="Desktop" className="w-6 h-6" />
            </ToggleGroupItem>
            <ToggleGroupItem value="mobile">
              <img src={PhoneIcon} alt="Phone" className="w-6 h-6" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div className="flex flex-grow flex-col items-center min-h-0">
          <AutoScale
            deviceWidth={deviceWidth}
            deviceHeight={deviceHeight}
            frameClassName="rounded-xl shadow-md"
            scaleBy={device === "desktop" ? "width" : "height"}
          >
            <div className="flex flex-col h-full">
              <div>
                {device === "desktop" ? <BrowserTop /> : <BrowserTopMobile />}
              </div>
              <iframe
                onLoad={() => setPreviewLoading(false)}
                ref={iframeRef}
                src={`/p/preview`}
                className="w-full h-full"
              />
              {previewLoading && (
                <div className="absolute flex items-center top-0 left-0 w-full h-full bg-gray-100">
                  <Loader message="Loading preview..." />
                </div>
              )}
            </div>
          </AutoScale>
        </div>
      </div>
    </div>
  )
}
